import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Monitor, Tablet, Loader2, LogOut, AlertTriangle, CheckCircle, MapPin, Clock, Trash2, AlertOctagon, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios, { API } from '@/utils/api';
import { useNavigate } from 'react-router-dom';

const SecuritySettings = ({ onLogout }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  
  // Account deletion state
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deletionData, setDeletionData] = useState(null);
  const [loadingDeletion, setLoadingDeletion] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/auth/sessions`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load active sessions');
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId, isCurrent) => {
    if (isCurrent) {
      if (!window.confirm('This will log you out of this device. Continue?')) {
        return;
      }
    }

    setRevoking(sessionId);
    try {
      const response = await axios.delete(`${API}/auth/sessions/${encodeURIComponent(sessionId)}`);
      
      if (response.data.was_current_session) {
        toast.success('Logged out successfully');
        onLogout?.();
      } else {
        toast.success('Session revoked');
        fetchSessions();
      }
    } catch (error) {
      console.error('Failed to revoke session:', error);
      toast.error('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const revokeAllOtherSessions = async () => {
    if (!window.confirm('This will log you out from all other devices. Continue?')) {
      return;
    }

    setRevokingAll(true);
    try {
      const response = await axios.delete(`${API}/auth/sessions?keep_current=true`);
      toast.success(response.data.message);
      fetchSessions();
    } catch (error) {
      console.error('Failed to revoke sessions:', error);
      toast.error('Failed to log out other devices');
    } finally {
      setRevokingAll(false);
    }
  };

  const getDeviceIcon = (deviceType) => {
    const type = deviceType?.toLowerCase() || '';
    if (type.includes('mobile') || type.includes('phone')) {
      return <Smartphone className="w-5 h-5" />;
    } else if (type.includes('tablet')) {
      return <Tablet className="w-5 h-5" />;
    }
    return <Monitor className="w-5 h-5" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--theme-primary)' }} />
      </div>
    );
  }

  const currentSession = sessions.find(s => s.is_current);
  const otherSessions = sessions.filter(s => !s.is_current);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className="p-2 rounded-lg"
            style={{ background: 'rgba(var(--theme-primary-rgb), 0.2)' }}
          >
            <Shield className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--theme-text)' }}>
              Active Sessions
            </h3>
            <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
              Manage where you're logged in
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowExplanation(!showExplanation)}
          className="text-xs underline"
          style={{ color: 'var(--theme-primary)' }}
        >
          Why is this important?
        </button>
      </div>

      {/* User-friendly explanation */}
      {showExplanation && (
        <div 
          className="p-4 rounded-xl text-sm"
          style={{ 
            background: 'rgba(var(--theme-primary-rgb), 0.1)',
            border: '1px solid rgba(var(--theme-primary-rgb), 0.2)'
          }}
        >
          <p className="font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
            Your journal is private. This helps keep it that way.
          </p>
          <p style={{ color: 'var(--theme-text-secondary)' }}>
            This shows every device where you're currently logged in. If you ever see a device you don't recognize 
            (like someone else accessing your account), you can instantly kick them out with one tap. 
            Think of it as a security camera for your account.
          </p>
        </div>
      )}

      {/* Current Session */}
      {currentSession && (
        <div 
          className="p-4 rounded-xl"
          style={{ 
            background: 'rgba(var(--theme-success-rgb, 34, 197, 94), 0.1)',
            border: '1px solid rgba(var(--theme-success-rgb, 34, 197, 94), 0.3)'
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div 
                className="p-2 rounded-lg"
                style={{ background: 'rgba(var(--theme-success-rgb, 34, 197, 94), 0.2)' }}
              >
                {getDeviceIcon(currentSession.device_type)}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium" style={{ color: 'var(--theme-text)' }}>
                    {currentSession.browser} on {currentSession.os}
                  </span>
                  <span 
                    className="px-2 py-0.5 rounded text-xs font-medium"
                    style={{ 
                      background: 'rgba(var(--theme-success-rgb, 34, 197, 94), 0.2)',
                      color: 'rgb(var(--theme-success-rgb, 34, 197, 94))'
                    }}
                  >
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    Current
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {currentSession.ip_address}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Active now
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Other Sessions */}
      {otherSessions.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>
              Other Devices ({otherSessions.length})
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={revokeAllOtherSessions}
              disabled={revokingAll}
              className="text-xs"
              style={{ color: 'var(--theme-error, #ef4444)' }}
            >
              {revokingAll ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <LogOut className="w-3 h-3 mr-1" />
              )}
              Log out all others
            </Button>
          </div>

          <div className="space-y-3">
            {otherSessions.map((session) => (
              <div 
                key={session.session_id}
                className="p-4 rounded-xl flex items-start justify-between"
                style={{ 
                  background: 'var(--theme-background-secondary)',
                  border: '1px solid rgba(var(--theme-border-rgb), 0.3)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{ background: 'rgba(var(--theme-text-secondary-rgb), 0.1)' }}
                  >
                    {getDeviceIcon(session.device_type)}
                  </div>
                  <div>
                    <span className="font-medium" style={{ color: 'var(--theme-text)' }}>
                      {session.browser} on {session.os}
                    </span>
                    <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {session.ip_address}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(session.last_used)}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => revokeSession(session.full_session_id, false)}
                  disabled={revoking === session.full_session_id}
                  className="text-xs"
                  style={{ color: 'var(--theme-error, #ef4444)' }}
                >
                  {revoking === session.full_session_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* No other sessions */}
      {otherSessions.length === 0 && (
        <div 
          className="text-center py-8 rounded-xl"
          style={{ 
            background: 'var(--theme-background-secondary)',
            border: '1px solid rgba(var(--theme-border-rgb), 0.2)'
          }}
        >
          <Shield className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
            You're only logged in on this device
          </p>
        </div>
      )}

      {/* Security Tips */}
      <div 
        className="p-4 rounded-xl"
        style={{ 
          background: 'rgba(var(--theme-warning-rgb, 245, 158, 11), 0.1)',
          border: '1px solid rgba(var(--theme-warning-rgb, 245, 158, 11), 0.3)'
        }}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: 'rgb(var(--theme-warning-rgb, 245, 158, 11))' }} />
          <div>
            <h4 className="font-medium text-sm" style={{ color: 'var(--theme-text)' }}>
              Security Tip
            </h4>
            <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>
              If you see a device you don't recognize, remove it immediately and change your password.
            </p>
          </div>
        </div>
      </div>

      {/* Danger Zone - Account Deletion */}
      <div className="mt-8 pt-6 border-t border-red-500/20">
        <button
          onClick={() => {
            if (!showDeleteSection) {
              initiateAccountDeletion();
            }
            setShowDeleteSection(!showDeleteSection);
          }}
          className="w-full flex items-center justify-between p-4 rounded-xl transition-colors hover:bg-red-500/10"
          style={{ 
            background: showDeleteSection ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}
        >
          <div className="flex items-center gap-3">
            <AlertOctagon className="w-5 h-5 text-red-500" />
            <div className="text-left">
              <h3 className="font-semibold text-red-500">Danger Zone</h3>
              <p className="text-xs text-red-400/70">Delete your account permanently</p>
            </div>
          </div>
          <span className="text-xs text-red-400">{showDeleteSection ? 'Hide' : 'Show'}</span>
        </button>

        {showDeleteSection && (
          <div className="mt-4 p-4 rounded-xl bg-red-500/5 border border-red-500/20">
            {loadingDeletion ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-red-500" />
              </div>
            ) : deletionData ? (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-sm font-medium text-red-400 mb-2">
                    ⚠️ This action is PERMANENT and cannot be undone
                  </p>
                  <p className="text-xs text-red-300/70">
                    All your data will be permanently deleted, including:
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 rounded-lg" style={{ background: 'var(--theme-background-secondary)' }}>
                    <p className="text-2xl font-bold text-red-400">{deletionData.data_summary?.journal_entries || 0}</p>
                    <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Entries</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: 'var(--theme-background-secondary)' }}>
                    <p className="text-2xl font-bold text-red-400">{deletionData.data_summary?.images || 0}</p>
                    <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Images</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: 'var(--theme-background-secondary)' }}>
                    <p className="text-2xl font-bold text-red-400">{deletionData.data_summary?.sessions || 0}</p>
                    <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Sessions</p>
                  </div>
                </div>

                {deletionData.requires_password && (
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-text-secondary)' }}>
                      Enter your password to confirm
                    </label>
                    <div className="relative">
                      <Input
                        type={showDeletePassword ? 'text' : 'password'}
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder="Your password"
                        className="pr-10 bg-red-500/5 border-red-500/30 text-red-200 placeholder:text-red-300/40"
                      />
                      <button
                        type="button"
                        onClick={() => setShowDeletePassword(!showDeletePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400/60 hover:text-red-400"
                      >
                        {showDeletePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-text-secondary)' }}>
                    Type <span className="font-mono text-red-400">DELETE MY ACCOUNT</span> to confirm
                  </label>
                  <Input
                    type="text"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="bg-red-500/5 border-red-500/30 text-red-200 placeholder:text-red-300/40"
                  />
                </div>

                <Button
                  onClick={handleDeleteAccount}
                  disabled={
                    deleting || 
                    confirmationText !== 'DELETE MY ACCOUNT' || 
                    (deletionData.requires_password && !deletePassword)
                  }
                  className="w-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting Account...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Permanently Delete My Account
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-red-400/60">
                  You will be logged out and redirected to the home page after deletion.
                </p>
              </div>
            ) : (
              <p className="text-center text-sm text-red-400/70 py-4">
                Failed to load deletion data. Please try again.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Function to initiate account deletion
  async function initiateAccountDeletion() {
    setLoadingDeletion(true);
    try {
      const response = await axios.post(`${API}/user/request-deletion`);
      setDeletionData(response.data);
    } catch (error) {
      console.error('Failed to initiate deletion:', error);
      toast.error('Failed to load account deletion data');
    } finally {
      setLoadingDeletion(false);
    }
  }

  // Function to handle account deletion
  async function handleDeleteAccount() {
    if (confirmationText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (deletionData?.requires_password && !deletePassword) {
      toast.error('Please enter your password');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/user/account`, {
        data: {
          confirmation: confirmationText,
          password: deletePassword || null
        }
      });
      
      toast.success('Your account has been deleted');
      
      // Clear local storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Redirect to home page
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
      
    } catch (error) {
      console.error('Failed to delete account:', error);
      const message = error.response?.data?.detail || 'Failed to delete account';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  }
};

export default SecuritySettings;
